import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  CheckCircle2, XCircle, AlertCircle, BookOpen, Loader2,
  RefreshCw, ChevronDown, ChevronUp, UserCheck
} from 'lucide-react';
import { toast } from 'sonner';
import OperationsLayout, { PageHeader } from './OperationsLayout';
import { getPromotionQueue, reviewPromotion } from '@/lib/operationsApi';
import { Button } from '@/components/ui/button';

const STATUS_TABS = [
  { value: "pending_review", label: "Pending Review" },
  { value: "needs_sme",      label: "Needs SME"      },
  { value: "approve",        label: "Approved"        },
  { value: "reject",         label: "Rejected"        },
];

export default function KnowledgeReviewPage() {
  const navigate = useNavigate();
  const [tab, setTab]         = useState("pending_review");
  const [items, setItems]     = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);
  const [reviewing, setReviewing] = useState(null);
  const [notes, setNotes]     = useState({});
  const [editedSummary, setEditedSummary] = useState({});

  const load = useCallback(() => {
    setLoading(true);
    getPromotionQueue(tab)
      .then(r => setItems(r.items || []))
      .catch(e => toast.error("Failed to load queue: " + e.message))
      .finally(() => setLoading(false));
  }, [tab]);

  useEffect(() => { load(); }, [load]);

  const handleReview = async (promotionId, action) => {
    setReviewing(promotionId + action);
    try {
      await reviewPromotion(promotionId, {
        action,
        reviewer_notes: notes[promotionId] || undefined,
        edited_summary: editedSummary[promotionId] || undefined,
      });
      const labels = { approve: "Approved — added to knowledge base", reject: "Rejected", needs_sme: "Marked as needs SME" };
      toast.success(labels[action] || action);
      load();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setReviewing(null);
    }
  };

  const isReadOnly = tab === "approve" || tab === "reject";

  return (
    <OperationsLayout>
      <PageHeader
        title="Knowledge Review Queue"
        subtitle="Review incidents nominated for the knowledge base"
        actions={
          <Button size="sm" variant="outline" onClick={load} disabled={loading}>
            <RefreshCw className="w-3.5 h-3.5" />
          </Button>
        }
      />

      <div className="p-6 space-y-4">

        {/* Tabs */}
        <div className="flex gap-1 border-b border-border">
          {STATUS_TABS.map(t => (
            <button
              key={t.value}
              onClick={() => setTab(t.value)}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors -mb-px ${
                tab === t.value
                  ? "border-primary text-primary"
                  : "border-transparent text-muted-foreground hover:text-foreground"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
          </div>
        ) : items.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 gap-2 text-muted-foreground">
            <BookOpen className="w-8 h-8" />
            <div className="text-sm">No items in this queue</div>
          </div>
        ) : (
          <div className="space-y-3">
            {items.map(p => {
              const isOpen = expanded === p.id;
              return (
                <div key={p.id} className="bg-card border border-border rounded-lg overflow-hidden">
                  {/* Header row */}
                  <button
                    className="w-full text-left px-4 py-3 flex items-center gap-3 hover:bg-accent transition-colors"
                    onClick={() => setExpanded(isOpen ? null : p.id)}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-medium text-foreground">
                          {[p.vendor, p.technology, p.network_function].filter(Boolean).join(" · ") || "Incident"}
                        </span>
                        <StatusChip status={p.status} />
                      </div>
                      <div className="text-xs text-muted-foreground mt-0.5 truncate">
                        {p.root_cause?.slice(0, 100)}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <button
                        className="text-xs text-primary hover:underline"
                        onClick={e => { e.stopPropagation(); navigate(`/operations/incidents/${p.incident_id}`); }}
                      >
                        View incident
                      </button>
                      {isOpen ? <ChevronUp className="w-4 h-4 text-muted-foreground" /> : <ChevronDown className="w-4 h-4 text-muted-foreground" />}
                    </div>
                  </button>

                  {/* Expanded detail */}
                  {isOpen && (
                    <div className="border-t border-border px-4 py-4 space-y-4">

                      {/* Summary */}
                      <div>
                        <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">Knowledge Summary</div>
                        {isReadOnly ? (
                          <div className="text-sm text-foreground whitespace-pre-wrap bg-muted/30 rounded p-3 font-mono text-xs">
                            {p.summary}
                          </div>
                        ) : (
                          <textarea
                            className="w-full text-sm border border-input rounded-md px-3 py-2 bg-background text-foreground resize-none font-mono text-xs"
                            rows={8}
                            value={editedSummary[p.id] !== undefined ? editedSummary[p.id] : p.summary}
                            onChange={e => setEditedSummary(s => ({ ...s, [p.id]: e.target.value }))}
                          />
                        )}
                      </div>

                      {/* Root cause / Resolution */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <div className="text-xs font-medium text-muted-foreground mb-1">Root Cause</div>
                          <div className="text-sm text-foreground">{p.root_cause}</div>
                        </div>
                        <div>
                          <div className="text-xs font-medium text-muted-foreground mb-1">Resolution</div>
                          <div className="text-sm text-foreground">{p.resolution}</div>
                        </div>
                      </div>

                      {p.lessons_learned && (
                        <div>
                          <div className="text-xs font-medium text-muted-foreground mb-1">Lessons Learned</div>
                          <div className="text-sm text-foreground">{p.lessons_learned}</div>
                        </div>
                      )}

                      {/* Reviewer notes from previous review */}
                      {p.reviewer_notes && (
                        <div className="bg-muted/30 rounded p-3">
                          <div className="text-xs font-medium text-muted-foreground mb-1">Reviewer Notes</div>
                          <div className="text-sm text-foreground">{p.reviewer_notes}</div>
                        </div>
                      )}

                      {/* Action area (only for pending/needs_sme) */}
                      {!isReadOnly && (
                        <div className="space-y-2 pt-2 border-t border-border">
                          <div>
                            <label className="text-xs text-muted-foreground mb-1 block">Reviewer Notes</label>
                            <textarea
                              className="w-full text-sm border border-input rounded-md px-3 py-2 bg-background text-foreground resize-none"
                              rows={2}
                              placeholder="Optional: feedback, corrections, conditions for approval"
                              value={notes[p.id] || ""}
                              onChange={e => setNotes(n => ({ ...n, [p.id]: e.target.value }))}
                            />
                          </div>
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              className="bg-green-600 hover:bg-green-700 text-white"
                              onClick={() => handleReview(p.id, "approve")}
                              disabled={!!reviewing}
                            >
                              {reviewing === p.id + "approve"
                                ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />
                                : <CheckCircle2 className="w-3.5 h-3.5 mr-1" />
                              }
                              Approve — Add to Knowledge Base
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleReview(p.id, "needs_sme")}
                              disabled={!!reviewing}
                            >
                              <UserCheck className="w-3.5 h-3.5 mr-1" />
                              Needs SME
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              className="border-red-200 text-red-600 hover:bg-red-50 dark:border-red-800 dark:text-red-400"
                              onClick={() => handleReview(p.id, "reject")}
                              disabled={!!reviewing}
                            >
                              {reviewing === p.id + "reject"
                                ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />
                                : <XCircle className="w-3.5 h-3.5 mr-1" />
                              }
                              Reject
                            </Button>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </OperationsLayout>
  );
}

function StatusChip({ status }) {
  const map = {
    pending_review: "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400",
    needs_sme:      "bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400",
    approve:        "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400",
    reject:         "bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400",
  };
  const labels = { pending_review: "Pending", needs_sme: "Needs SME", approve: "Approved", reject: "Rejected" };
  return (
    <span className={`text-xs px-1.5 py-0.5 rounded font-medium ${map[status] || ""}`}>
      {labels[status] || status}
    </span>
  );
}
